import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLanguage } from '../../shared/i18n';
import { selectAuth, selectUser, setUser } from '../../core/auth/authSlice';
import { useAppDispatch } from '../../app/hooks';
import http from '../../shared/utils/http';
import httpData from '../../shared/utils/httpData';
import './LanguageSelector.scss';

interface Language {
  id: string; // ISO 639-1 code (e.g., "en", "it")
  name: string;
  nativeName: string;
  isDefault: boolean;
}

interface LanguagesResponse {
  languages: Language[];
  defaultLanguage: string;
}

const LanguageSelector: React.FC = () => {
  const { currentLanguage, changeLanguage, isLoading } = useLanguage();
  const [languages, setLanguages] = useState<Language[]>([]);
  const [loadingLanguages, setLoadingLanguages] = useState(true);
  const isAuthenticated = useSelector(selectAuth);
  const user = useSelector(selectUser);
  const dispatch = useAppDispatch();

  // Fetch available languages from backend
  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const response = await fetch(`${httpData.url}/i18n/languages`);
        if (response.ok) {
          const data: LanguagesResponse = await response.json();
          setLanguages(data.languages || []);
        }
      } catch (error) {
        console.error('Failed to fetch languages:', error);
        setLanguages([]);
      } finally {
        setLoadingLanguages(false);
      }
    };

    fetchLanguages();
  }, []);

  // Sync language with user preference when authenticated
  useEffect(() => {
    if (isAuthenticated && user?.preferredLanguage && user.preferredLanguage !== currentLanguage) {
      changeLanguage(user.preferredLanguage as any);
    }
  }, [isAuthenticated, user?.preferredLanguage]);

  const handleLanguageChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newLang = e.target.value;

    // Change language in i18n
    await changeLanguage(newLang as any);

    // Save preference to backend if authenticated
    if (isAuthenticated) {
      try {
        await http.patch('/members/me', { preferredLanguage: newLang });
        // Update Redux store
        dispatch(setUser({ ...user, preferredLanguage: newLang }));
      } catch (error) {
        console.error('Failed to save language preference:', error);
      }
    }
  };

  if (loadingLanguages) {
    return null;
  }

  if (!languages || languages.length === 0) {
    return null;
  }

  return (
    <div className="language-selector">
      <select
        value={currentLanguage}
        onChange={handleLanguageChange}
        disabled={isLoading}
        className="language-selector__select"
      >
        {languages.map((lang) => (
          <option key={lang.id} value={lang.id}>
            {lang.nativeName}
          </option>
        ))}
      </select>
    </div>
  );
};

export default LanguageSelector;
